import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { Search, ChevronLeft, ChevronRight, Loader2, ShoppingCart, Home, Bell, BellDot, Settings, Share2, ShoppingBag, Users } from 'lucide-react';
import { Capacitor } from '@capacitor/core';
import MobileBottomNav from "../../components/app_components/mobile/MobileBottomNav";
import DashboardHeader from "../../components/app_components/DashboardHeader";
import { useTranslation } from "../../contexts/LanguageContext";
import { useAuth } from "../../App"; 

const MobileMarketplaceCategory = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { category } = useParams();
  const { t } = useTranslation();
  const { user, logout, validateToken, httpClient } = useAuth();
  
  const [searchQuery, setSearchQuery] = useState('');
  const [merchants, setMerchants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isNativePlatform, setIsNativePlatform] = useState(false);
  
  // Add premium status state
  const [isPremium, setIsPremium] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(8);

  const [unreadCount, setUnreadCount] = useState(0);

  // Decode category for display
  const decodedCategory = decodeURIComponent(category || '');

  // Check if user is authenticated
  useEffect(() => {
    if (!user) {
      navigate("/demo");
    }
  }, [user, navigate]);

  // Check if running on native platform
  useEffect(() => {
    setIsNativePlatform(Capacitor.isNativePlatform());
  }, []);

  const fetchUnreadNotifications = useCallback(async () => {
    try {
      if (!user) return;

      const response = await httpClient.request('/api/notifications');
      
      if (!response.ok) {
        if (response.status === 401) {
          const revalidated = await validateToken();
          if (!revalidated) {
            navigate("/demo");
            return;
          }
          return fetchUnreadNotifications();
        }
        throw new Error('Failed to fetch notifications');
      }

      const notifications = await response.json();
      const unread = notifications.filter(notif => !notif.read_status).length;
      setUnreadCount(unread);
    } catch (err) {
      console.error('Error fetching notifications:', err);
      if (err.status === 401) {
        navigate("/demo");
      }
    }
  }, [user, httpClient, validateToken, navigate]);

  useEffect(() => {
    if (user) {
      fetchUnreadNotifications();
    }
  }, [user, fetchUnreadNotifications]);

  // Fetch premium status
  const fetchPremiumStatus = async () => {
    try {
      if (!user) {
        setIsPremium(false);
        return;
      }

      // Try dedicated premium status endpoint
      try {
        const premiumRes = await httpClient.request('/api/user/premium-status');
        if (premiumRes.ok) {
          const premiumData = await premiumRes.json();
          setIsPremium(premiumData.isPremium);
          return;
        }
      } catch (premiumError) {
        console.error('Error fetching premium status:', premiumError);
      }

      // Fallback to user account endpoint
      const fallbackRes = await httpClient.request('/api/user/account');
      if (fallbackRes.ok) {
        const fallbackData = await fallbackRes.json();
        setIsPremium(fallbackData.userPlan === 'premium');
      }
    } catch (error) {
      console.error('Error fetching user premium status:', error);
      setIsPremium(false);
    }
  };

  const navItems = [
    { icon: Home, label: t('bottomNav.home') || "Home", path: "/mobile-dashboard" },
    { 
      icon: unreadCount > 0 ? BellDot : Bell,
      label: t('bottomNav.notifications') || "Notifications",
      path: "/mobile-notifications",
      badge: unreadCount > 0 ? unreadCount : null
    },
    { icon: ShoppingCart, label: t('bottomNav.marketplace') || "Marketplace", path: "/mobile-marketplace" },
    { icon: Share2, label: t('bottomNav.shareSubs') || "Share Subs", path: "/mobile-share-subs" },
    { icon: ShoppingBag, label: t('bottomNav.connectBank') || "Connect Bank", path: "/mobile-connectBank" },
    { icon: Users, label: t('bottomNav.partners') || "Partners", path: "/mobile-partners" },
    { icon: Settings, label: t('bottomNav.settings') || "Settings", path: "/mobile-account-settings" },
  ];

  // Fetch merchants by category
  const fetchMerchantsByCategory = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // IMPORTANT: Use httpClient which handles URL construction properly
      const response = await httpClient.request(`/api/marketplace-category?category=${encodeURIComponent(decodedCategory)}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success) {
        setMerchants(data.merchants || []);
      } else {
        throw new Error(data.error || "Failed to load category");
      }
    } catch (err) {
      setError(err.message || 'Failed to load category');
      console.error("Category fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [decodedCategory, httpClient]);

  useEffect(() => {
    if (user && decodedCategory) {
      fetchMerchantsByCategory();
      fetchPremiumStatus(); // Fetch premium status
    }
  }, [user, decodedCategory, fetchMerchantsByCategory]);

  // Ad banner component - only show on web, not on native iOS/Android
  const AdBanner = () => {
    if (isNativePlatform) {
      return null;
    }

    return (
      <div className="bg-white rounded-xl p-4 my-4 shadow-sm border border-blue-100">
        <h3 className="text-sm font-medium text-gray-500 mb-2">{t('marketplaceCategory.sponsored') || "Sponsored"}</h3>
        <a
          rel="sponsored"
          href="https://www.awin1.com/cread.php?s=3975182&v=115715&q=516084&r=1966723"
          className="block"
        >
          <img
            src="https://www.awin1.com/cshow.php?s=3975182&v=115715&q=516084&r=1966723"
            alt="eSIM for International Travel"
            className="w-full rounded-lg"
            onError={(e) => {
              e.target.src = "https://via.placeholder.com/800x200?text=Sponsored+Ad";
            }}
          />
        </a>
      </div>
    );
  };

  // Format price for display
  const formatPrice = (price) => {
    return price % 1 === 0 ? price : price.toFixed(2);
  };

  // Pagination logic
  const filteredMerchants = merchants.filter((merchant) =>
    merchant.name.toLowerCase().includes(searchQuery.toLowerCase())
  );
  
  // Calculate pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentMerchants = filteredMerchants.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredMerchants.length / itemsPerPage);

  // Change page
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Mobile Merchant Card Component
  const MerchantCard = React.memo(({ merchant }) => {
    const lowestPricePlan = merchant.plans?.reduce((lowest, plan) => 
      (plan.price < lowest.price ? plan : lowest), merchant.plans?.[0] || { price: Infinity });

    return (
      <div 
        onClick={() => navigate(`/mobile-merchant/${merchant.id}`)}
        className="border border-[#253985]/20 rounded-lg bg-white cursor-pointer hover:shadow-md transition-all"
      >
        <div className="p-3 flex flex-col h-full">
          <div className="flex justify-center mb-2">
            <img 
              src={merchant.logo} 
              alt={merchant.name}
              className="w-12 h-12 rounded-lg object-contain border border-[#253985]/10"
              onError={(e) => e.target.src = 'https://via.placeholder.com/80'}
            />
          </div>
          
          <h3 className="text-[#253985] text-center mb-1 text-sm font-medium line-clamp-2 leading-tight">
            {merchant.name}
          </h3>
          
          {merchant.description && (
            <p className="text-xs text-gray-600 text-center line-clamp-2 mb-2 leading-tight">
              {merchant.description}
            </p>
          )}
          
          {lowestPricePlan && lowestPricePlan.price < Infinity && (
            <div className="mt-auto text-center">
              <span className="bg-[#253985]/10 text-[#253985] px-2 py-1 rounded-full text-[10px] font-medium">
                {t('marketplace.from') || "From"} {lowestPricePlan.currency}{formatPrice(lowestPricePlan.price)}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  });

  // Mobile Pagination Controls
  const Pagination = () => {
    if (totalPages <= 1) return null;
    
    return (
      <div className="flex justify-center items-center mt-4 space-x-1">
        <button
          onClick={() => paginate(Math.max(1, currentPage - 1))}
          disabled={currentPage === 1}
          className={`p-1 rounded-lg ${
            currentPage === 1 
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
              : 'bg-white text-[#253985]'
          }`}
        >
          <ChevronLeft className="h-4 w-4" />
        </button>
        
        <span className="text-sm text-gray-700">
          {t('marketplaceCategory.page') || "Page"} {currentPage} {t('marketplaceCategory.of') || "of"} {totalPages}
        </span>
        
        <button
          onClick={() => paginate(Math.min(totalPages, currentPage + 1))}
          disabled={currentPage === totalPages}
          className={`p-1 rounded-lg ${
            currentPage === totalPages
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
              : 'bg-white text-[#253985]'
          }`}
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    );
  };

  return (
    <div className="flex flex-col h-screen bg-gray-50">
      <DashboardHeader isAuthenticated={!!user} user={user} onLogout={logout} />
      
      <div className="flex-1 overflow-auto pb-20">
        <div className="p-4 space-y-4">
          {/* Informative Header Block with conditional premium gradient */}
          <div className={`${isPremium 
            ? 'bg-gradient-to-r from-[#253985] to-amber-500' 
            : 'bg-[#253985]'} text-white p-4 rounded-lg text-center transition-all duration-500`}>
            <h1 className="text-lg font-semibold mb-1">{t('marketplace.title') || "Subscription Marketplace"}</h1>
            <p className="text-white/90 text-sm">{t('marketplace.subtitle') || "Discover and manage your favorite subscriptions"}</p>
          </div>

          <div className="flex items-center">
            <button
              onClick={() => navigate("/mobile-marketplace")}
              className="flex items-center text-[#253985]"
            >
              <ChevronLeft className="h-5 w-5 mr-1" />
              <span className="text-sm">{t('common.back') || "Back"}</span>
            </button>
            <h1 className="text-xl font-bold ml-2 text-gray-800 truncate">
              {decodedCategory}
            </h1>
          </div>

          {/* Search Bar */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder={(t('marketplaceCategory.searchPlaceholderMobile') || "Search") + " " + decodedCategory + "..."}
              // FIX: Changed from text-sm to text-base to prevent iOS zoom
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-base"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          {/* Results Info */}
          {!loading && !error && (
            <div className="text-sm text-gray-500">
              {filteredMerchants.length} {filteredMerchants.length === 1 ? 
                (t('marketplaceCategory.result') || "result") : 
                (t('marketplaceCategory.results') || "results")}
            </div>
          )}

          {/* Content Area */}
          {loading ? (
            <div className="flex justify-center items-center h-40">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h3 className="text-red-800 font-medium text-sm">{t('marketplaceCategory.loadErrorTitle') || "Error loading category"}</h3>
              <p className="text-red-600 mt-1 text-sm">{error}</p>
              <button
                onClick={fetchMerchantsByCategory}
                className="mt-2 text-sm text-red-600 hover:text-red-800 font-medium"
              >
                {t('marketplace.tryAgain') || "Try again"}
              </button>
            </div>
          ) : (
            <>
              {filteredMerchants.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-500">{t('marketplaceCategory.noMerchantsFound') || "No merchants found"}</p>
                  <button
                    onClick={() => navigate("/mobile-marketplace")}
                    className="mt-4 bg-[#253985] hover:bg-[#1a2c6d] text-white px-4 py-2 rounded-lg text-sm"
                  >
                    {t('marketplaceCategory.browseAll') || "Browse All Categories"}
                  </button>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    {currentMerchants.map((merchant) => (
                      <MerchantCard key={merchant.id} merchant={merchant} />
                    ))}
                  </div>
                  
                  {/* Ad Banner - Conditionally rendered based on platform */}
                  <AdBanner />
                  
                  <Pagination />
                  
                  {/* Results count footer */}
                  <div className="text-center text-gray-500 text-sm mt-4">
                    {Math.min(indexOfFirstItem + 1, filteredMerchants.length)}-{Math.min(indexOfLastItem, filteredMerchants.length)} {t('marketplaceCategory.of') || "of"} {filteredMerchants.length} {t('marketplaceCategory.results') || "results"}
                  </div>
                </>
              )}
            </>
          )}
        </div>
      </div>

      {/* Bottom Navigation */}
      <MobileBottomNav 
        navItems={navItems} 
        currentPath={location.pathname}
        maxVisibleItems={5}
        navigate={navigate}
        className="fixed bottom-0 w-full"
      />
    </div>
  );
};

export default MobileMarketplaceCategory;